import React, { useEffect, useMemo, useState } from 'react';
import { FlightSelect } from '@flybits/design-system';
import { convertEpochToTime } from 'helpers/common.helper';
import { getTimezoneList, guessUserTimezone } from 'helpers/templated-experience.helper';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import { Option, SelectOption } from 'types/common';
import { getIsAudienceEmpty, selectStepRule } from 'store/rule/rule.selector';
import { saveContextualSchedulerAction } from 'store/rule/rule.action';
import { getAbsoluteScheduleErrors } from 'validators/ExperienceCanvas/rule.validator';
import { PUSH_ACTION_TYPES } from 'store/push/push.type';
import { selectSchedulesFromDraft } from '../scheduleDraft.helper';
import { ScheduleBasedOn, ScheduleFormProps } from '../scheduleDraft.types';
import EventsSchedule from './EventsSchedule/EventsSchedule';
import TimeSchedule from './TimeSchedule/TimeSchedule';
import { MAIN_CLASS, CLASSES } from './classes';
import './ScheduleForm.scss';

const basedOnOptions: SelectOption[] = [
  { key: 'time', name: 'Time' },
  { key: 'events', name: 'Events' },
  { key: 'nothing', name: 'Nothing' },
];

const tzList: Option[] = getTimezoneList().map((tz) => ({
  key: tz.key,
  name: tz.name,
  value: tz.offset,
}));

const ScheduleForm: React.FC<ScheduleFormProps> = ({ stepIdx, draft, draftDispatch, isJourneyLocked }) => {
  const isScheduleDisabled = useSelector((state) => {
    const pushRefId = state.te.journey.steps[stepIdx]?.push;

    if (!pushRefId) {
      return false;
    }

    const pushStateItem = state.te.push.byRefId[pushRefId];
    const isBroadcast = pushStateItem?.actionType === PUSH_ACTION_TYPES.BROADCAST_PUSH;

    const step = state.te.journey.steps[stepIdx];
    const isReminder = step?.constraints?.audienceConstraints?.syncStartScheduleWithRuleBody ?? false;

    return isBroadcast || isReminder;
  });
  const { status, schedule: journeySchedule } = useSelector((state) => state.te.journey);

  const startScheduler = draft.startScheduler;
  const endScheduler = draft.endScheduler;
  const operationType = draft.step.operationType;

  const isAudienceEveryone = useSelector((state) => {
    const primaryRule = selectStepRule(state.te, stepIdx);
    return !primaryRule?.enableOptionalNode;
  });

  const storeDateTime = useMemo(() => {
    const stepSchedule = selectSchedulesFromDraft(draft);
    const timezone = stepSchedule.start?.timezone || stepSchedule.end?.timezone || guessUserTimezone();
    const start = convertEpochToTime(stepSchedule.start?.value, timezone, draft.step.omitStart);
    const end = convertEpochToTime(stepSchedule.end?.value, timezone, draft.step.omitEnd);
    return {
      start,
      end,
      timezone: tzList?.find((tz) => tz.name === timezone),
    };
  }, [draft]);

  const err = useSelector((state) => {
    const stepSchedule = selectSchedulesFromDraft(draft);
    const timezone = stepSchedule.start?.timezone || stepSchedule.end?.timezone || guessUserTimezone();
    const start = convertEpochToTime(stepSchedule.start?.value, timezone, draft.step.omitStart);
    const end = convertEpochToTime(stepSchedule.end?.value, timezone, draft.step.omitEnd);

    if (status === 'active' || status === 'scheduled') {
      return { start: [], end: [], journey: [] };
    }

    return getAbsoluteScheduleErrors(
      state,
      {
        isActive: !!start.isActive,
        value: stepSchedule.start?.value,
        hasCronExpression: !!stepSchedule?.start?.cronExpression,
      },
      { isActive: !!end.isActive, value: stepSchedule.end?.value },
      journeySchedule,
    );
  });

  const [dateTime, setDateTime] = useState(storeDateTime);

  useEffect(() => {
    setDateTime(storeDateTime);
  }, [storeDateTime]);

  const clearDateTime = () => {
    setDateTime({
      start: {
        isActive: true,
        date: undefined,
        time: undefined,
      },
      end: {
        isActive: false,
        date: undefined,
        time: undefined,
      },
      timezone: dateTime.timezone,
    });
  };

  const getBasedOnInitialValue = () => {
    const seededBasedOn = draft.step.scheduleBasedOn;
    if (seededBasedOn) return basedOnOptions.find((option) => option.key === seededBasedOn);
    if (Object.keys(startScheduler?.predicates || {}).length > 0) return basedOnOptions[1];
    if (!!dateTime.start.date || !!dateTime.end.date || !!startScheduler?.cronExpression) return basedOnOptions[0];
    return undefined;
  };

  const [basedOn, setBasedOn] = useState<SelectOption | undefined>(getBasedOnInitialValue);

  const handleBasedOnChange = (option: SelectOption) => {
    if (isBasedOnDisabled) return;
    // both event-driven and turned-off schedules leave the absolute start/end unevaluated
    const omit = option.key !== 'time';

    draftDispatch({
      type: 'CLEAR_SCHEDULE',
      payload: {
        stepIdx,
        type: 'both',
      },
    });

    draftDispatch({
      type: 'UPDATE_STEP',
      payload: {
        stepIdx,
        fields: {
          omitSched: omit,
          omitStart: omit,
          omitEnd: omit,
          scheduleBasedOn: option.key as ScheduleBasedOn,
        },
      },
    });

    if (option.key === 'time') {
      draftDispatch(
        saveContextualSchedulerAction({
          stepIdx,
          type: 'both',
          modifier: '',
          predicates: null,
          target: 'now',
          logic: '',
        }),
      );
    } else {
      clearDateTime();
    }

    setBasedOn(option);
  };

  useEffect(() => {
    if (isAudienceEveryone) {
      draftDispatch({
        type: 'UPDATE_STEP',
        payload: {
          stepIdx,
          fields: {
            omitSched: true,
            omitStart: true,
            omitEnd: true,
          },
        },
      });
    }
  }, [isAudienceEveryone, draftDispatch, stepIdx]);

  const isAudienceEmpty = useSelector((state) => getIsAudienceEmpty(state.te, stepIdx));

  const isBasedOnDisabled = isAudienceEveryone || isAudienceEmpty || isScheduleDisabled || isJourneyLocked;
  const showScheduleSections = !isAudienceEveryone && !!basedOn?.key && basedOn.key !== 'nothing';

  return (
    <div className={MAIN_CLASS}>
      {isJourneyLocked && <div className={CLASSES.LOCKED_OVERLAY} />}
      <div className={CLASSES.SECTION_HIGHLIGHT}>
        <div className={CLASSES.FIELD}>
          <label htmlFor="schedule-type">The schedule will be based on</label>
          <FlightSelect
            ariaControlsId="schedule-type"
            options={basedOnOptions}
            selected={
              (basedOn || {
                key: '',
                name: (
                  <span className={CLASSES.SELECT_PLACEHOLDER}>
                    Select how the schedule will work <span className={CLASSES.REQUIRED_SYMBOL}>*</span>
                  </span>
                ),
              }) as SelectOption
            }
            handleOptionClick={handleBasedOnChange}
            width="100%"
            disabled={isBasedOnDisabled}
          />
        </div>
      </div>
      {showScheduleSections && basedOn?.key === 'time' && (
        <TimeSchedule
          stepIdx={stepIdx}
          storeDateTime={storeDateTime}
          dateTime={dateTime}
          startScheduler={startScheduler}
          endScheduler={endScheduler}
          ruleDraft={draft.rule}
          operationType={operationType}
          setDateTime={setDateTime}
          clearDateTime={clearDateTime}
          errors={err}
          draftDispatch={draftDispatch}
        />
      )}
      {showScheduleSections && basedOn?.key === 'events' && (
        <EventsSchedule
          stepIdx={stepIdx}
          dateTime={dateTime}
          errors={err}
          startScheduler={startScheduler}
          endScheduler={endScheduler}
          setDateTime={setDateTime}
          draftDispatch={draftDispatch}
        />
      )}
    </div>
  );
};

export default ScheduleForm;
